import React from 'react';
import type {
  ModuleEnum,
  TaskAssignmentResponse,
  TaskDefinitionResponse,
} from 'src/api/ProactApiClient';
import {TaskDefinitionStateEnum} from 'src/api/ProactApiClient';
import {FormTransitionBar} from 'src/app/forms/FormTransitionBar';
import {FormViewComponentV2} from 'src/app/forms/FormViewComponent/FormViewComponentV2';
import {useTaskAssignmentTransition} from 'src/app/tasks/offline/useTaskAssignmentTransition';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import type {IFormDataV2, IFormFieldV2} from 'src/types/FormV2';
import type {IStateMachineTransition} from 'src/types/StateMachine';

export interface ActionContentsProps {
  module: ModuleEnum;
  formData: IFormDataV2;
  formMeta: IFormFieldV2[];
  item?: TaskDefinitionResponse;
  assignment?: TaskAssignmentResponse;
  onRefreshTransition: () => void;
  onOfflineQueued: () => void;
  /** Replaces redirect navigation after a transition, e.g. to close a drawer. */
  onRedirect?: (transition: IStateMachineTransition) => void;
}

/**
 * Action body: read-only details plus the "My Actions" and "Manage Action" transition bars.
 * @registry Action view content with assignment and definition transition bars.
 */
export const ActionContents: React.FC<ActionContentsProps> = ({
  module,
  formData,
  formMeta,
  item,
  assignment,
  onRefreshTransition,
  onOfflineQueued,
  onRedirect,
}) => {
  const postTransition = useTaskAssignmentTransition(assignment?.id);
  const [taskAssignmentMachine] = useStateMachine({
    url: ApiEndpoints.getTaskAssignmentStateMachine,
  });
  const [taskDefinitionMachine] = useStateMachine({
    url: ApiEndpoints.getTaskDefinitionStateMachine,
  });

  return (
    <>
      <FormViewComponentV2 formData={formData} formMeta={formMeta} />
      {taskAssignmentMachine &&
        assignment &&
        item &&
        item.state !== TaskDefinitionStateEnum.Draft &&
        item.locked === false && (
          <FormTransitionBar
            showActionBar={true}
            title="My Actions"
            description="Use the actions below to update your specific status for this action."
            module={module}
            stateMachine={taskAssignmentMachine}
            stateId={assignment.state}
            transitionBaseUrl={`task-assignment/${assignment.id}/transition`}
            postTransition={postTransition}
            onOfflineQueued={onOfflineQueued}
            onRedirect={onRedirect}
            onRefreshTransition={onRefreshTransition}
            objectType={assignment?.taskDefinition?.taskType}
            bypassPermissions={true}
            invalidateTagsOnComplete={['TaskAssignments']}
          />
        )}
      {taskDefinitionMachine && item && item.locked === false && (
        <FormTransitionBar
          showActionBar={true}
          title="Manage Action"
          description="Use the actions below to update all statuses for this action, including for other people."
          module={module}
          stateMachine={taskDefinitionMachine}
          stateId={item.state}
          transitionBaseUrl={`task-definition/${item.id}/transition`}
          onRedirect={onRedirect}
          onRefreshTransition={onRefreshTransition}
          objectType={item?.taskType}
          bypassPermissions={true}
          invalidateTagsOnComplete={['TaskDefinitions', 'TaskAssignments']}
        />
      )}
    </>
  );
};
